import { useEffect, useRef, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import type { ClientDashboard, ClientDetail } from '@/core/models/practice.models';
import type { ReportSettingsView } from '@/core/models/report-options.models';
import type { CompanyInfoSettings } from '@/core/models/report-settings.models';
import type { EmailLogRow, ReportRow } from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { filesNothingAtCompaniesHouse, useClientType } from '@/core/useClientType';
import { selectedClient, useSelectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { reportOptionsService } from '@/core/services/reportOptions';
import { reportSettingsService } from '@/core/services/reportSettings';
import { reportsService } from '@/core/services/reports';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { ClientFinancialOverviewWidget } from './ClientFinancialOverview';
import { Icon } from '@/kit/Icon';
import { DashboardToolbar, DashboardZones } from '@/kit/dashboard/DashboardLayout';
import { useDashboardLayout, type WidgetDef } from '@/kit/dashboard/useDashboardLayout';
import { openDialog } from '@/kit/dialog';
import { REPORT_NOT_PRODUCIBLE } from '@/features/tr-reports/accountsFiling';
import { REPORT_PDF_PREVIEW_DIALOG, ReportPdfPreview } from '@/features/tr-reports/ReportPdfPreview';
import './TaxreturnDashboard.scss';

/** APT378: the widgets the client dashboard offers; the order inside a zone is the default order (option A). */
export const CLIENT_DASHBOARD_WIDGETS: WidgetDef[] = [
  { id: 'keyFigures', title: 'Key figures', zone: 'top', removable: false },
  { id: 'details', title: 'Client Details', zone: 'upper', removable: true },
  { id: 'financial', title: 'Financial Overview', zone: 'upper', removable: true },
  { id: 'annual', title: 'Annual Reports', zone: 'lowerMain', removable: true },
  { id: 'management', title: 'Management Reports', zone: 'lowerMain', removable: true },
  { id: 'activity', title: 'Recent activity', zone: 'lowerSide', removable: true },
];

/** Where the layout (order, hidden widgets, lock) is remembered, per browser. */
export const CLIENT_DASHBOARD_LAYOUT_KEY = 'ap.dashboard.layout.client';

interface DashboardTile {
  label: string;
  value: number;
  icon: string;
  link: string;
}

/** BUG-078: a sole trader or partnership files no CT600 (a partnership's return is an SA800), so its dashboard has no such tile. */
const hasNoCt600 = (clientType: string | null | undefined) =>
  /^(sole trader|partnership)$/i.test(clientType?.trim() ?? '');

/** Tiles about filing deadlines, which do not apply to a sole trader or partnership (no accounts are filed). */
const SOLE_TRADER_HIDDEN_TILES = ['Accounts due in 30 days', 'Accounts overdue', 'CT600 due in 30 days'];

function buildTiles(summary: ClientDashboard, noFiling: boolean): DashboardTile[] {
  const tiles: DashboardTile[] = [
    {
      label: 'Open accounting periods',
      value: summary.openPeriods,
      icon: 'event_available',
      link: '/home/taxreturns/tasks-accountingperiods',
    },
    {
      label: 'Accounts due in 30 days',
      value: summary.accountsDueIn30Days,
      icon: 'schedule',
      link: '/home/taxreturns/tasks-accountingperiods',
    },
    { label: 'Accounts overdue', value: summary.accountsOverdue, icon: 'report_problem', link: '' },
    { label: 'CT600 due in 30 days', value: summary.ct600DueIn30Days, icon: 'receipt_long', link: '' },
    {
      label: 'Trial balances',
      value: summary.trialBalances,
      icon: 'balance',
      link: '/home/taxreturns/tasks-trialbalance',
    },
    // APT378: the detailed list of unbalanced trial balances is gone; this count opens the client's Trial Balance screen,
    // which reads the filter from the address and lists only the trial balances that are out of balance.
    {
      label: 'Trial balances out of balance',
      value: summary.unbalancedTrialBalances,
      icon: 'warning_amber',
      link: '/home/taxreturns/tasks-trialbalance?filter=unbalanced',
    },
    { label: 'Locked periods', value: summary.lockedPeriods, icon: 'lock', link: '' },
    {
      label: 'Chart of accounts',
      value: summary.chartAccounts,
      icon: 'list_alt',
      link: '/home/taxreturns/settings-accoununts',
    },
  ];
  if (noFiling) return tiles.filter((tile) => !SOLE_TRADER_HIDDEN_TILES.includes(tile.label));
  return hasNoCt600(summary.clientType) ? tiles.filter((tile) => tile.label !== 'CT600 due in 30 days') : tiles;
}

type Overdue = ClientDashboard['overduePeriodList'][number];
type Activity = ClientDashboard['recentActivity'][number];

const overdueColumns: DataTableColumn<Overdue>[] = [
  { id: 'period', header: 'Accounting period', cell: (row) => row.period },
  { id: 'due', header: 'Accounts due', cell: (row) => row.dueDate },
  {
    id: 'overdue',
    header: 'Days overdue',
    cell: (row) => <span className="text-danger-100">{row.daysOverdue}</span>,
  },
];

const emailColumns: DataTableColumn<EmailLogRow>[] = [
  { id: 'date', header: 'When', cell: (row) => dayjs(row.createdAtUtc).format('DD-MMM-YYYY hh:mm A') },
  { id: 'subject', header: 'Subject', cell: (row) => row.subject },
  { id: 'to', header: 'Sent to', cell: (row) => row.sentTo },
  { id: 'status', header: 'Status', cell: (row) => row.status },
];

const activityColumns: DataTableColumn<Activity>[] = [
  // BUG-056: the instant is shown in the viewer's time zone, exactly as the firm dashboard and the logs do.
  { id: 'date', header: 'When', cell: (row) => dayjs(row.occurredAtUtc).format('DD-MMM-YYYY hh:mm A') },
  { id: 'description', header: 'Activity', cell: (row) => row.description },
  { id: 'user', header: 'User', cell: (row) => row.userName },
];

/** Number of reports each dashboard list shows; "All Reports" opens the full list. */
const REPORT_LIMIT = 5;

interface DetailRow {
  label: string;
  value: string;
  /** Spans two columns of the card grid (a long address line). */
  wide?: boolean;
}

type EmailLoad = { kind: 'idle' | 'loading' | 'error' } | { kind: 'ready'; rows: EmailLogRow[] };

const DUE_CHIPS: [DueChip, string][] = [
  ['all', 'All'],
  ['overdue', 'Overdue'],
  ['due', 'Due'],
];

const dash = (value: string | null | undefined) => (value && value.trim() ? value : '-');

const MONTH_NUMBERS: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

/** The dates written in a text as yyyy-MM-dd, in order: the lists show them as dd/MM/yyyy or dd-MMM-yyyy. */
function datesIn(text: string | null | undefined): string[] {
  const found: { at: number; iso: string }[] = [];
  for (const m of (text ?? '').matchAll(/(\d{2})\/(\d{2})\/(\d{4})/g))
    found.push({ at: m.index ?? 0, iso: `${m[3]}-${m[2]}-${m[1]}` });
  for (const m of (text ?? '').matchAll(/(\d{2})-([A-Za-z]{3})-(\d{4})/g)) {
    const month = MONTH_NUMBERS[m[2].toLowerCase()];
    if (month) found.push({ at: m.index ?? 0, iso: `${m[3]}-${month}-${m[1]}` });
  }
  return found.sort((a, b) => a.at - b.at).map((d) => d.iso);
}

const periodKey = (from: string | null | undefined, to: string | null | undefined) =>
  `${datesIn(from)[0] ?? from}|${datesIn(to)[0] ?? to}`;

type DueChip = 'all' | 'overdue' | 'due';
type SubmissionFilter = 'all' | 'submitted' | 'notSubmitted';

/** The submit status the report list sends: 1 is Submitted. */
const isSubmitted = (row: ReportRow) => row.submitStatusId === 1;

/** A titled panel with the 3.0 minimise button. */
function DashboardPanel({
  title,
  icon,
  actions,
  children,
}: {
  title: string;
  icon: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="dashboard-panel">
      <div className="dashboard-panel-header d-flex align-items-center justify-space-between">
        <div className="d-flex align-items-center gap-10">
          <Icon name={icon} outlined />
          <h2 className="m-0">{title}</h2>
        </div>
        <div className="d-flex align-items-center gap-10">
          {actions}
          <button
            type="button"
            className="dashboard-panel-toggle"
            aria-label={open ? `Minimise ${title}` : `Expand ${title}`}
            onClick={() => setOpen((value) => !value)}
          >
            <Icon name={open ? 'remove' : 'add'} />
          </button>
        </div>
      </div>
      {open && <div className="dashboard-panel-body">{children}</div>}
    </div>
  );
}

/** Angular TaxreturnDashboardComponent: the Accounts dashboard of the client that is open. */
export function TaxreturnDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ClientDashboard | null>(null);
  // APT244: the client shown is the app-wide selected one, not a copy kept by this page, so switching it here moves the
  // Accounts shell's client bar (and every other client screen) along with it.
  const client = useSelectedClient();
  /** The client the latest load asked for: a slower answer about the previous client must not land on this one. */
  const loadingFor = useRef<string | null>(null);
  // BUG-057: the 3.0 client dashboard's Client Details panel and the Annual / Management report lists.
  const [details, setDetails] = useState<ClientDetail | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoSettings | null>(null);
  const [reportSettings, setReportSettings] = useState<ReportSettingsView | null>(null);
  const [annualReports, setAnnualReports] = useState<ReportRow[]>([]);
  const [managementReports, setManagementReports] = useState<ReportRow[]>([]);
  // APT378: the Annual Reports filters (over the rows already loaded) and the Activity / Email log tabs.
  const [dueChip, setDueChip] = useState<DueChip>('all');
  const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilter>('all');
  const [activityTab, setActivityTab] = useState<'activity' | 'email'>('activity');
  const [emailLog, setEmailLog] = useState<EmailLoad>({ kind: 'idle' });
  const layout = useDashboardLayout(CLIENT_DASHBOARD_WIDGETS, CLIENT_DASHBOARD_LAYOUT_KEY);

  const load = () => {
    // Re-read the selected client every time: it is resolved from a cookie that may have appeared since.
    const current = selectedClient();
    loadingFor.current = current?.id ?? null;

    if (!current) {
      notifyError('Open a client from Accounts to see its dashboard.');
      return;
    }
    const stillCurrent = () => loadingFor.current === current.id;
    practiceService.getClientDashboard(current.id).then(
      (result) => {
        if (!stillCurrent()) return;
        setSummary(result);
      },
      (error) => {
        if (stillCurrent()) notifyError(error);
      },
    );

    // The panels below are extras: one that cannot be read leaves just that panel empty rather than the whole page.
    const keep = <T,>(request: Promise<T>, apply: (value: T) => void) =>
      request.then(
        (value) => {
          if (stillCurrent()) apply(value);
        },
        () => undefined,
      );
    void keep(practiceService.getClient(current.id), setDetails);
    void keep(reportSettingsService.getCompanyInfo(current.id), setCompanyInfo);
    void keep(reportOptionsService.getReportSettings(current.id), setReportSettings);
    void keep(reportsService.getReports(current.id, 0), setAnnualReports);
    void keep(reportsService.getReports(current.id, 1), setManagementReports);
  };

  // APT244: when the selected client changes, what the previous one showed is cleared at once, so its details never
  // sit under the new client's name while the new dashboard loads.
  const [shownFor, setShownFor] = useState(client?.id);
  if (shownFor !== client?.id) {
    setShownFor(client?.id);
    setSummary(null);
    setDetails(null);
    setCompanyInfo(null);
    setReportSettings(null);
    setAnnualReports([]);
    setManagementReports([]);
    setDueChip('all');
    setSubmissionFilter('all');
    setActivityTab('activity');
    setEmailLog({ kind: 'idle' });
  }

  // APT244: (re)load whenever the selected client changes - on opening the page, from the dropdown or from elsewhere.
  useEffect(() => {
    load();
  }, [client?.id]);

  // APT378: the Email log tab reads the client's own log (last 30 days) the first time it is opened.
  const showEmailLog = () => {
    setActivityTab('email');
    if (!client || emailLog.kind !== 'idle') return;
    const clientId = client.id;
    setEmailLog({ kind: 'loading' });
    const done = (next: EmailLoad) => {
      if (selectedClient()?.id === clientId) setEmailLog(next);
    };
    try {
      void Promise.resolve(reportsService.getEmailLogs({ search: null, filter: 0, clientId })).then(
        (rows) => done({ kind: 'ready', rows: rows ?? [] }),
        () => done({ kind: 'error' }),
      );
    } catch {
      setEmailLog({ kind: 'error' });
    }
  };

  const noFiling = filesNothingAtCompaniesHouse(useClientType(client?.id));
  const tiles = summary ? buildTiles(summary, noFiling) : [];
  const overduePeriods = summary?.overduePeriodList ?? [];
  const recentActivity = summary?.recentActivity ?? [];

  const primaryAddress = details?.addresses.find((a) => a.isPrimary) ?? details?.addresses[0];
  // Issue #42: a partnership or sole trader is not registered, so it has no registration number to show.
  const hasRegistrationNumber = !noFiling && !hasNoCt600(summary?.clientType);
  const clientManager = details?.clientManager?.trim() ?? '';
  const detailRows: DetailRow[] = [
    { label: 'Name', value: dash(details?.companyName ?? summary?.clientName) },
    ...(hasRegistrationNumber
      ? [{ label: 'Registration Number', value: dash(details?.registrationNumber ?? companyInfo?.registrationNumber) }]
      : []),
    { label: 'Registered In', value: dash(companyInfo?.registeredIn) },
    { label: 'Trading Status', value: reportSettings ? (reportSettings.tradingStatus === 1 ? 'Dormant' : 'Trading') : '-' },
    { label: 'Audit Status', value: reportSettings ? (reportSettings.auditStatus === 2 ? 'Audited' : 'Unaudited') : '-' },
    // APT378: Year End, UTR and VAT Number are shown only when the client has them.
    ...(details?.yearEnd?.trim() ? [{ label: 'Year End', value: details.yearEnd }] : []),
    ...(details?.utrNo?.trim() ? [{ label: 'UTR', value: details.utrNo }] : []),
    ...(details?.vatRegNo?.trim() ? [{ label: 'VAT Number', value: details.vatRegNo }] : []),
    // APT378: the address and postcode sit in the same Client Details card, not in a separate column on the right.
    { label: 'Address', value: dash(primaryAddress?.addressLine), wide: true },
    { label: 'City', value: dash(primaryAddress?.city) },
    { label: 'Postcode', value: dash(primaryAddress?.postCode) },
  ];

  // APT378: Annual Reports filters. The report rows carry no due date, so a row is Overdue when its period is one of the
  // dashboard's periods past their accounts due date and it is not yet submitted; Due is every other row not yet submitted.
  const overdueKeys = new Set(
    overduePeriods.map((period) => {
      const [from, to] = datesIn(period.period);
      return `${from}|${to}`;
    }),
  );
  const annualShown = annualReports.filter((row) => {
    const overdue = !isSubmitted(row) && overdueKeys.has(periodKey(row.fromDate, row.toDate));
    if (dueChip === 'overdue' && !overdue) return false;
    if (dueChip === 'due' && (isSubmitted(row) || overdue)) return false;
    if (submissionFilter === 'submitted') return isSubmitted(row);
    if (submissionFilter === 'notSubmitted') return !isSubmitted(row);
    return true;
  });
  const editClient = () => {
    if (client) navigate(matrixPath('/home/manage', 'addClient', { id: btoa(client.id), from: 'dashboard' }));
  };
  const openReport = (_type: number, row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    openDialog(ReportPdfPreview, {
      data: { reportId: row.id, refNo: row.refNo },
      ...REPORT_PDF_PREVIEW_DIALOG,
    });
  };

  const reportColumns = (type: 0 | 1, extra: DataTableColumn<ReportRow>[]): DataTableColumn<ReportRow>[] => [
    {
      id: 'ref',
      header: 'Ref. No.',
      cell: (row: ReportRow) => (
        <a
          href="#"
          className="link"
          onClick={(event) => {
            event.preventDefault();
            openReport(type, row);
          }}
        >
          {row.refNo}
        </a>
      ),
    },
    ...extra,
  ];

  // APT378: each widget of the customisable layout (see CLIENT_DASHBOARD_WIDGETS). Without a client there are none.
  const keyFigures = (
    <div className="card-grid dashboard-strip">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className={`card${tile.link ? ' cursor-pointer' : ''}`}
          onClick={() => {
            if (tile.link) {
              navigate(tile.link);
            }
          }}
        >
          <div className="card-body">
            <div className="card-img">
              <Icon name={tile.icon} outlined />
            </div>
            <div className="card-text">
              <h2>{tile.label}</h2>
              <span>{tile.value}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const detailsWidget = (
    <DashboardPanel
      title="Client Details"
      icon="apartment"
      actions={
        <button type="button" className="dashboard-panel-toggle" aria-label="Edit client" onClick={editClient}>
          <Icon name="edit_note" outlined />
        </button>
      }
    >
      <div className="client-details">
        <dl>
          {detailRows.map(({ label, value, wide }) => (
            <div key={label} className={`client-details-row${wide ? ' client-details-wide' : ''}`}>
              <dt>{`${label} :`}</dt>
              <dd className="text-ellipsis" title={value}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </DashboardPanel>
  );

  const annualWidget = (
    <>
      <DashboardPanel title="Annual Reports" icon="code">
        <div className="annual-filters">
          <div className="annual-chips" role="group" aria-label="Filter annual reports">
            {DUE_CHIPS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`annual-chip${dueChip === id ? ' on' : ''}`}
                aria-pressed={dueChip === id}
                onClick={() => setDueChip(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="annual-select-label">
            Submission status
            <select
              className="annual-select"
              value={submissionFilter}
              onChange={(event) => setSubmissionFilter(event.target.value as SubmissionFilter)}
            >
              <option value="all">All</option>
              <option value="submitted">Submitted</option>
              <option value="notSubmitted">Not submitted</option>
            </select>
          </label>
        </div>
        <DataTable
          columns={reportColumns(0, [
            { id: 'from', header: 'From', cell: (row: ReportRow) => row.fromDate },
            { id: 'to', header: 'To', cell: (row: ReportRow) => row.toDate },
            { id: 'type', header: 'Type', cell: (row: ReportRow) => row.type },
            { id: 'description', header: 'Description', cell: (row: ReportRow) => row.description },
            { id: 'status', header: 'Submission Status', cell: (row: ReportRow) => dash(row.submitStatus) },
          ])}
          rows={annualShown.slice(0, REPORT_LIMIT)}
          rowKey={(row) => row.id}
        />
        {annualReports.length === 0 && <div className="text-align-center p-1">No annual reports yet.</div>}
        {annualReports.length > 0 && annualShown.length === 0 && (
          <div className="text-align-center p-1">No annual reports match this filter.</div>
        )}
        <div className="text-align-right p-1">
          <a
            href="#"
            className="link"
            onClick={(event) => {
              event.preventDefault();
              navigate('/home/taxreturns/reports-accounts');
            }}
          >
            All Reports
          </a>
        </div>
      </DashboardPanel>
      {!noFiling && (
        <div className="dashboard-overdue">
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <h2 className="m-0">Accounts past their due date</h2>
            </div>
          </div>
          <div className="mat-table-wrapper">
            <DataTable
              columns={overdueColumns}
              rows={overduePeriods}
              rowKey={(row, index) => `${row.accountingPeriodId}-${index}`}
              rowClassName={() => 'cursor-pointer'}
              rowTitle={() => "Open this client's accounting periods"}
              onRowClick={() => navigate('/home/taxreturns/tasks-accountingperiods')}
              stickyHeader
            />
            {overduePeriods.length === 0 && (
              <div className="text-align-center p-1">No accounting period is past its accounts due date.</div>
            )}
          </div>
        </div>
      )}
    </>
  );

  const managementWidget = (
    <DashboardPanel title="Management Reports" icon="calendar_month">
      <DataTable
        columns={reportColumns(1, [
          { id: 'from', header: 'From Date', cell: (row: ReportRow) => row.fromDate },
          { id: 'to', header: 'To Date', cell: (row: ReportRow) => row.toDate },
          { id: 'description', header: 'Description', cell: (row: ReportRow) => row.description },
        ])}
        rows={managementReports.slice(0, REPORT_LIMIT)}
        rowKey={(row) => row.id}
      />
      {managementReports.length === 0 && <div className="text-align-center p-1">No management reports yet.</div>}
      <div className="text-align-right p-1">
        <a
          href="#"
          className="link"
          onClick={(event) => {
            event.preventDefault();
            navigate('/home/taxreturns/reports-managementaccounts');
          }}
        >
          All Reports
        </a>
      </div>
    </DashboardPanel>
  );

  const activityWidget = (
    <div className="dashboard-activity">
      <div className="content-header mb-0">
        <div className="content-header-left mb-1 d-flex align-items-center gap-15">
          <h2 className="m-0">Recent activity</h2>
          <div className="annual-chips" role="group" aria-label="Activity view">
            <button
              type="button"
              className={`annual-chip${activityTab === 'activity' ? ' on' : ''}`}
              aria-pressed={activityTab === 'activity'}
              onClick={() => setActivityTab('activity')}
            >
              Activity
            </button>
            <button
              type="button"
              className={`annual-chip${activityTab === 'email' ? ' on' : ''}`}
              aria-pressed={activityTab === 'email'}
              onClick={showEmailLog}
            >
              Email log
            </button>
          </div>
        </div>
      </div>
      {activityTab === 'activity' ? (
        <div className="mat-table-wrapper no-hover">
          <DataTable
            columns={activityColumns}
            rows={recentActivity}
            rowKey={(row, index) => `${row.id}-${index}`}
            stickyHeader
          />
          {recentActivity.length === 0 && (
            <div className="text-align-center p-1">There is no recorded activity for this client yet.</div>
          )}
        </div>
      ) : (
        <div className="mat-table-wrapper no-hover" data-testid="client-email-log">
          <DataTable
            columns={emailColumns}
            rows={emailLog.kind === 'ready' ? emailLog.rows.slice(0, REPORT_LIMIT) : []}
            rowKey={(row) => row.id}
            stickyHeader
          />
          {emailLog.kind === 'error' && (
            <div className="text-align-center p-1">Email log is not available right now.</div>
          )}
          {emailLog.kind === 'ready' && emailLog.rows.length === 0 && (
            <div className="text-align-center p-1">No emails were sent for this client in the last 30 days.</div>
          )}
          <div className="text-align-right p-1">
            <a
              href="#"
              className="link"
              onClick={(event) => {
                event.preventDefault();
                navigate('/home/taxreturns/emaillogs');
              }}
            >
              View all emails
            </a>
          </div>
        </div>
      )}
    </div>
  );

  const widgetContent: Record<string, ReactNode> = client
    ? {
        keyFigures,
        details: detailsWidget,
        financial: <ClientFinancialOverviewWidget clientId={client.id} />,
        annual: annualWidget,
        management: managementWidget,
        activity: activityWidget,
      }
    : {};

  return (
    <div className="form wp-100 tr-dashboard client-dashboard taxreturn-dashboard" data-testid="client-dashboard">
      <div className="content-body">
        <div className="content-header mb-0">
          <div
            className="content-header-left mb-1 d-flex align-items-center gap-15 client-dropdown-container"
            data-testid="client-dropdown-container"
          >
            <h2
              className="m-0 client-title client-name text-ellipsis"
              data-testid="client-name-heading"
              title={summary ? summary.clientName : client ? client.name : undefined}
            >
              {summary ? summary.clientName : client ? client.name : 'Accounts'}
            </h2>
            {summary?.clientType && <span className="client-type-chip">{summary.clientType}</span>}
            {/* APT378: the manager is shown only when the client carries one; the API does not send it yet. */}
            {clientManager && (
              <div className="client-manager" data-testid="client-manager">
                <span className="client-manager-label">Client manager</span>
                <b className="client-manager-name">{clientManager}</b>
              </div>
            )}
          </div>
          {client && (
            <div className="content-header-right mb-1">
              <DashboardToolbar layout={layout} />
            </div>
          )}
        </div>

        {!client && (
          <div className="info-message primary-message mb-1">
            No client is selected. Open a client from Accounts to see its work.
          </div>
        )}
        {/* APT378: the line under the client name that repeated the overdue and out-of-balance counts is gone - the tiles below show them. */}

        {client && (
          <div className="client-dash-zones">
            <DashboardZones
              layout={layout}
              wideIds={['keyFigures']}
              zoneClassName={{
                top: 'client-zone-top',
                upper: 'client-zone-upper',
                lowerMain: 'client-zone-main',
                lowerSide: 'client-zone-side',
              }}
              renderWidget={(widget) => widgetContent[widget.id] ?? null}
            />
          </div>
        )}
      </div>
    </div>
  );
}
